import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon, ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, Search as SearchIcon, Clear as ClearIcon, ArrowForward as ArrowForwardIcon, InfoOutlined as InfoOutlinedIcon, SearchOff as SearchOffIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// List of JSON files - add new files here when you add them to the public/upload_log/json folder
const JSON_FILES = [
  "CAMEROON_globaldata_log_22May_updated.json",
"NIGERIA_globaldata_log_24May_updated.json",
"ETHIOPIA_globaldata_log_23May_updated.json",
"GHANA_globaldata_log_22May_updated.json",
"PARAGUAY_globaldata_log_26May_updated.json",
"IVORY_COAST_globaldata_log_23May.json",
"RWANDA_globaldata_log_24May.json",
"KENYA_globaldata_log_24May.json",
"SOUTH_SUDAN_globaldata_log_24May.json",
"LESOTHO_globaldata_log_24May_updated.json",
"TANZANIA_globaldata_log_24May.json",
"MOLDOVA_globaldata_log_23May.json",
"UKRAINE_globaldata_log_23May_updated.json",
"NAMIBIA_globaldata_log_23May.json",
"BOTSWANA_globaldata_log_22May.json",
"BURUNDI_globaldata_log_21May_updated.json",
"CHILE_globaldata_log_21May_updated.json",
"SRILANKA_globaldata_log_26May_updated.json",
"PANAMA_globaldata_log_26May_updated.json",
"PHILIPPINES_globaldata_log_26May_updated.json",
"VENEZUELA_globaldata_log_26May_updated.json",
"BRAZIL_globaldata_log_20May.json",
"URUGUAY_globaldata_log_26May.json",
"RUSSIA_globaldata_log_22May_updated.json",
"LIBERIA_globaldata_log_21May.json",
"COLOMBIA_globaldata_log_21May_updated.json",
"ZIMBABWE_globaldata_log_27May.json",
"UZBEKISTAN_globaldata_log_27May.json",
"NICARAGUA_globaldata_log_27May.json",
"KAZAKHSTAN_globaldata_log_27May.json"
];

const cardStyles = {
  borderRadius: 2,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '& .MuiCardContent-root': {
    padding: 3
  }
};

const statCardStyles = {
  bgcolor: 'background.paper',
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  }
};

const accordionStyles = {
  '& .MuiAccordionSummary-root': {
    bgcolor: 'background.default',
    borderRadius: '8px',
    '&.Mui-expanded': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    }
  },
  '& .MuiAccordionDetails-root': {
    bgcolor: 'background.paper',
    pt: 3
  }
};

const fileCardStyles = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
    borderColor: 'primary.main',
    '& .MuiIconButton-root': {
      backgroundColor: 'primary.main',
      color: 'white'
    }
  }
};

const searchBarStyles = {
  position: 'relative',
  width: '100%',
  maxWidth: 600,
  mb: 4,
  '& .MuiInputBase-root': {
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  }
};

const Reports = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [summaryStats, setSummaryStats] = useState(null);
  const [imExSummary, setImExSummary] = useState(null);
  const [chapterSummary, setChapterSummary] = useState(null);
  const [yearSummary, setYearSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ status: '', progress: 0 });
  const reportCache = useRef(new Map());

  // Check authentication on component mount
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const getType = (report) => {
    if (report.chapType) {
      return report.chapType == "EX" ? "EX" : "IM"
    } else {
      if (report.filePath.includes("/IMPORT/")) {
        return "IM";
      } else if (report.filePath.includes("/EXPORT/")) {
        return "EX";
      } else {
        return "UNKNOWN";
      }
    }
  }

  const calculateSummaries = useCallback((processedData) => {
    setLoadingProgress({ status: 'Calculating summary statistics...', progress: 25 });
    
    // Calculate summary statistics
    const summary = processedData.reduce((acc, report) => {
      acc.totalFiles++;
      acc.totalExcel += report.totalExcelRecords || 0;
      acc.totalJson += report.totalJSONRecords || 0;
      acc.elasticSuccess += report.elasticSuccessCount || 0;
      acc.elasticFailure += report.elasticFailureCount || 0;
      acc.successCount += report.successs ? 1 : 0;
      acc.failureCount += report.successs ? 0 : 1;
      return acc;
    }, {
      totalFiles: 0,
      totalExcel: 0,
      totalJson: 0,
      elasticSuccess: 0,
      elasticFailure: 0,
      successCount: 0,
      failureCount: 0,
    });

    setLoadingProgress({ status: 'Processing import/export data...', progress: 50 });

    // Calculate IM/EX summaries
    const imEx = processedData.reduce((acc, report,idx) => {
      const type =  getType(report);
      // report.chapType?.toUpperCase() === "EX" ? "EX" : report.chapType?.toUpperCase() === "IM" ? "IM" : ;
      if (!acc[type]) {
        acc[type] = { 
          totalFiles: 0, 
          totalExcel: 0, 
          totalJson: 0, 
          elasticSuccess: 0, 
          elasticFailure: 0,
          successCount: 0,
          failureCount: 0 
        };
      }
      acc[type].totalFiles++;
      acc[type].totalExcel += report.totalExcelRecords || 0;
      acc[type].totalJson += report.totalJSONRecords || 0;
      acc[type].elasticSuccess += report.elasticSuccessCount || 0;
      acc[type].elasticFailure += report.elasticFailureCount || 0;
      acc[type].successCount += report.successs ? 1 : 0;
      acc[type].failureCount += report.successs ? 0 : 1;
      return acc;
    }, {});

    // Log the final counts
    console.log('Import/Export Summary:', JSON.stringify(imEx, null, 2));

    setLoadingProgress({ status: 'Analyzing chapter data...', progress: 75 });

    // Calculate chapter-wise and year-wise summaries with enhanced chapter details
    const { chapterWise, yearWise } = processedData.reduce((acc, report) => {
      // Extract year from the report
      const year = report.date ? new Date(report.date).getFullYear() :
                  report.filePath ? report.filePath.match(/\d{4}/) ? report.filePath.match(/\d{4}/)[0] : 'Unknown' :
                  'Unknown';

      // Chapter-wise processing with year breakdown
      const type =  getType(report);
      const chapter = report.chapter || "Unknown";

      if (!acc.chapterWise[type]) acc.chapterWise[type] = {};
      if (!acc.chapterWise[type][chapter]) {
        acc.chapterWise[type][chapter] = {
          count: 0,
          totalExcel: 0,
          totalJson: 0,
          elasticSuccess: 0,
          elasticFailure: 0,
          yearlyBreakdown: {}, // Add yearly breakdown
          successCount: 0,
          failureCount: 0
        };
      }

      // Update overall chapter stats
      acc.chapterWise[type][chapter].count++;
      acc.chapterWise[type][chapter].totalExcel += report.totalExcelRecords || 0;
      acc.chapterWise[type][chapter].totalJson += report.totalJSONRecords || 0;
      acc.chapterWise[type][chapter].elasticSuccess += report.elasticSuccessCount || 0;
      acc.chapterWise[type][chapter].elasticFailure += report.elasticFailureCount || 0;
      acc.chapterWise[type][chapter].successCount += report.successs ? 1 : 0;
      acc.chapterWise[type][chapter].failureCount += report.successs ? 0 : 1;

      // Update yearly breakdown for the chapter
      if (!acc.chapterWise[type][chapter].yearlyBreakdown[year]) {
        acc.chapterWise[type][chapter].yearlyBreakdown[year] = {
          count: 0,
          totalExcel: 0,
          totalJson: 0,
          elasticSuccess: 0,
          elasticFailure: 0
        };
      }
      acc.chapterWise[type][chapter].yearlyBreakdown[year].count++;
      acc.chapterWise[type][chapter].yearlyBreakdown[year].totalExcel += report.totalExcelRecords || 0;
      acc.chapterWise[type][chapter].yearlyBreakdown[year].totalJson += report.totalJSONRecords || 0;
      acc.chapterWise[type][chapter].yearlyBreakdown[year].elasticSuccess += report.elasticSuccessCount || 0;
      acc.chapterWise[type][chapter].yearlyBreakdown[year].elasticFailure += report.elasticFailureCount || 0;

      // Year-wise processing (existing code)
      if (!acc.yearWise[year]) {
        acc.yearWise[year] = {
          IM: { count: 0, totalExcel: 0, totalJson: 0, elasticSuccess: 0, elasticFailure: 0, successCount: 0, failureCount: 0 },
          EX: { count: 0, totalExcel: 0, totalJson: 0, elasticSuccess: 0, elasticFailure: 0, successCount: 0, failureCount: 0 }
        };
      }

      acc.yearWise[year][type].count++;
      acc.yearWise[year][type].totalExcel += report.totalExcelRecords || 0;
      acc.yearWise[year][type].totalJson += report.totalJSONRecords || 0;
      acc.yearWise[year][type].elasticSuccess += report.elasticSuccessCount || 0;
      acc.yearWise[year][type].elasticFailure += report.elasticFailureCount || 0;
      acc.yearWise[year][type].successCount += report.successs ? 1 : 0;
      acc.yearWise[year][type].failureCount += !report.successs ? 1 : 0;

      return acc;
    }, { chapterWise: {}, yearWise: {} });

    return { summary, imEx, chapterWise, yearWise };
  }, []);

  const handleFileSelect = async (filename) => {
    try {
      setLoading(true);
      setLoadingProgress({ status: 'Initializing...', progress: 0 });

      // Check cache first
      if (reportCache.current.has(filename)) {
        const cachedData = reportCache.current.get(filename);
        setSelectedFile(filename);
        setReportData(cachedData.processedData);
        setSummaryStats(cachedData.summary);
        setImExSummary(cachedData.imEx);
        setChapterSummary(cachedData.chapterWise);
        setYearSummary(cachedData.yearWise);
        setError('');
        setLoading(false);
        return;
      }

      setLoadingProgress({ status: 'Fetching report data...', progress: 10 });
      const response = await fetch(`/upload_log/json/${filename}`);
      if (!response.ok) {
        throw new Error('Failed to fetch report data');
      }

      const data = await response.json();
      const processedData = Array.isArray(data) ? data : [data];
      processedData.pop(0);

      // Calculate all summaries
      const summaries = calculateSummaries(processedData);

      // Cache the results
      reportCache.current.set(filename, {
        processedData,
        ...summaries
      });

      setSelectedFile(filename);
      setReportData(processedData);
      setSummaryStats(summaries.summary);
      setImExSummary(summaries.imEx);
      setChapterSummary(summaries.chapterWise);
      setYearSummary(summaries.yearWise);
      setError('');
    } catch (error) {
      console.error('Error loading report:', error);
      setError('Error loading report data');
    } finally {
      setLoading(false);
      setLoadingProgress({ status: '', progress: 0 });
    }
  };

  const handleBack = () => {
    setSelectedFile(null);
    setReportData(null);
    setSummaryStats(null);
    setImExSummary(null);
    setChapterSummary(null);
    setYearSummary(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    navigate('/login', { replace: true });
  };

  const FileList = () => {
    const [searchQuery, setSearchQuery] = useState('');

    const getCountryName = (filename) => {
      return filename.split('_')[0].replace(/_/g, ' ');
    };

    const getDate = (filename) => {
      const dateMatch = filename.match(/(\d{2}[A-Za-z]{3})/);
      return dateMatch ? dateMatch[1] : '';
    };

    const filteredFiles = JSON_FILES.filter(filename => {
      const countryName = getCountryName(filename);
      const date = getDate(filename);
      const searchLower = searchQuery.toLowerCase();
      
      return countryName.toLowerCase().includes(searchLower) ||
             filename.toLowerCase().includes(searchLower) ||
             date.toLowerCase().includes(searchLower);
    });

    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Upload Report Files
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {filteredFiles.length} of {JSON_FILES.length} reports available
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleLogout}
              startIcon={<ClearIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={searchBarStyles}>
          <TextField
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by country, filename, or date..."
            variant="outlined"
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ 
                      bgcolor: 'grey.100',
                      '&:hover': {
                        bgcolor: 'grey.200'
                      }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Grid container spacing={3}>
          {filteredFiles.length > 0 ? (
            filteredFiles.map((filename) => {
              const countryName = getCountryName(filename);
              const date = getDate(filename);
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={filename}>
                  <Card 
                    sx={fileCardStyles}
                    onClick={() => handleFileSelect(filename)}
                  >
                    <CardContent sx={{ p: 3, flex: 1 }}>
                      <Box sx={{ 
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        justifyContent: 'space-between',
                        gap: 2
                      }}>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600,
                              color: 'primary.main',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {countryName}
                          </Typography>
                          <Chip
                            label={date}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ minWidth: 80, textAlign: 'center' }}
                          />
                        </Box>
                        <IconButton 
                          size="small" 
                          sx={{ 
                            bgcolor: 'grey.100',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white'
                            }
                          }}
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                      </Box>

                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 2
                        }}
                      >
                        {filename}
                      </Typography>

                      <Box sx={{ 
                        mt: 'auto', 
                        pt: 2, 
                        borderTop: 1, 
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                          View Details
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              >
                <SearchOffIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Matching Reports
                </Typography>
                <Typography color="text.secondary">
                  Try adjusting your search criteria
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const SummarySection = ({ summaryStats, imExSummary, chapterSummary, yearSummary }) => {
    const [expandedPanel, setExpandedPanel] = useState(false);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

    return (
      <Box sx={{ mb: 4 }}>
        {/* Overall Statistics */}
        <Card sx={{ ...cardStyles, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600, mb: 3 }}>
              Overall Statistics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={2}>
                <Paper elevation={0} sx={statCardStyles}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 500 }}>
                    {summaryStats.totalFiles}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Total Files
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={2}>
                <Paper elevation={0} sx={statCardStyles}>
                  <Typography variant="h4" color="success.main" sx={{ fontWeight: 500 }}>
                    {summaryStats.successCount}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Successful
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={2}>
                <Paper elevation={0} sx={statCardStyles}>
                  <Typography variant="h4" color="error.main" sx={{ fontWeight: 500 }}>
                    {summaryStats.failureCount}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Failed
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={statCardStyles}>
                  <Typography variant="h4" color="info.main" sx={{ fontWeight: 500 }}>
                    {summaryStats.totalExcel}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Excel Records
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper elevation={0} sx={statCardStyles}>
                  <Typography variant="h4" color="info.main" sx={{ fontWeight: 500 }}>
                    {summaryStats.totalJson}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    JSON Records
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Import/Export Summary */}
        <Card sx={{ ...cardStyles, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600, mb: 3 }}>
              Import/Export Summary
            </Typography>
            <Grid container spacing={3}>
              {console.log("imExSummary::>",imExSummary)}
              {Object.entries(imExSummary).map(([type, stats]) => (
                <Grid item xs={12} md={6} key={type}>
                  <Paper sx={{ ...statCardStyles, p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                      {type === 'IM' ? 'Import' : 'Export'} Statistics
                    </Typography>
                    <List dense sx={{ '& .MuiListItem-root': { py: 1 } }}>
                      <ListItem>
                        <ListItemText 
                          primary={`Files: ${stats.totalFiles}`}
                          secondary={`Success Rate: ${((stats.successCount / stats.totalFiles) * 100).toFixed(2)}%`}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary={`Success/Failure: ${stats.successCount}/${stats.failureCount}`}
                          secondary={`Total Files: ${stats.totalFiles}`}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary={`Excel Records: ${stats.totalExcel}`}
                          secondary={`JSON Records: ${stats.totalJson}`}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary={`Elastic Success: ${stats.elasticSuccess}`}
                          secondary={`Elastic Failure: ${stats.elasticFailure}`}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Year-wise Summary */}
        <Card sx={{ ...cardStyles, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600, mb: 3 }}>
              Year-wise Summary
            </Typography>
            <Grid container spacing={3}>
              {Object.entries(yearSummary || {}).map(([year, typeStats]) => (
                <Grid item xs={12} key={year}>
                  <Accordion
                    expanded={expandedPanel === `year-${year}`}
                    onChange={handleAccordionChange(`year-${year}`)}
                    sx={accordionStyles}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Year {year}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3}>
                        {Object.entries(typeStats).map(([type, stats]) => (
                          <Grid item xs={12} md={6} key={type}>
                            <Paper sx={{ ...statCardStyles, p: 3 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                                {type === 'IM' ? 'Import' : 'Export'} Statistics
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Total Files: <Typography component="span" color="text.primary" fontWeight={500}>{stats.count}</Typography>
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Success/Failure: <Typography component="span" color="text.primary" fontWeight={500}>{stats.successCount}/{stats.failureCount}</Typography>
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Excel Records: <Typography component="span" color="text.primary" fontWeight={500}>{stats.totalExcel}</Typography>
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    JSON Records: <Typography component="span" color="text.primary" fontWeight={500}>{stats.totalJson}</Typography>
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="body2" color="text.secondary">
                                    Elastic Success/Failure: <Typography component="span" color="text.primary" fontWeight={500}>{stats.elasticSuccess}/{stats.elasticFailure}</Typography>
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Chapter-wise Summary */}
        <Card sx={{ ...cardStyles }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600, mb: 3 }}>
              Chapter-wise Summary
            </Typography>

            {/* Combine and process chapters from both IM and EX */}
            {(() => {
              const combinedChapters = new Set();
              Object.values(chapterSummary).forEach(typeData => {
                Object.keys(typeData).forEach(chapter => combinedChapters.add(chapter));
              });

              return Array.from(combinedChapters).sort().map(chapter => (
                <Accordion
                  key={chapter}
                  expanded={expandedPanel === `chapter-${chapter}`}
                  onChange={handleAccordionChange(`chapter-${chapter}`)}
                  sx={accordionStyles}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Chapter: {chapter}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      {/* Import/Export Overview */}
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                                Export Statistics
                              </Typography>
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Files: <Typography component="span" color="text.primary" fontWeight={500}>
                                    {chapterSummary?.EX?.[chapter]?.count || 0}
                                  </Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Excel Records: <Typography component="span" color="text.primary" fontWeight={500}>
                                    {chapterSummary?.EX?.[chapter]?.totalExcel || 0}
                                  </Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  JSON Records: <Typography component="span" color="text.primary" fontWeight={500}>
                                    {chapterSummary?.EX?.[chapter]?.totalJson || 0}
                                  </Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Success/Failure: <Typography component="span" color="success.main" fontWeight={500}>
                                    {chapterSummary?.EX?.[chapter]?.successCount || 0}
                                  </Typography>
                                  /<Typography component="span" color="error.main" fontWeight={500}>
                                    {chapterSummary?.EX?.[chapter]?.failureCount || 0}
                                  </Typography>
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                                Import Statistics
                              </Typography>
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Files: <Typography component="span" color="text.primary" fontWeight={500}>
                                    {chapterSummary?.IM?.[chapter]?.count || 0}
                                  </Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Excel Records: <Typography component="span" color="text.primary" fontWeight={500}>
                                    {chapterSummary?.IM?.[chapter]?.totalExcel || 0}
                                  </Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  JSON Records: <Typography component="span" color="text.primary" fontWeight={500}>
                                    {chapterSummary?.IM?.[chapter]?.totalJson || 0}
                                  </Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Success/Failure: <Typography component="span" color="success.main" fontWeight={500}>
                                    {chapterSummary?.IM?.[chapter]?.successCount || 0}
                                  </Typography>
                                  /<Typography component="span" color="error.main" fontWeight={500}>
                                    {chapterSummary?.IM?.[chapter]?.failureCount || 0}
                                  </Typography>
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>

                      {/* Yearly Breakdown */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                          Yearly Breakdown
                        </Typography>
                        <Grid container spacing={3}>
                          {/* Export Yearly Breakdown */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Export
                            </Typography>
                            <Grid container spacing={1}>
                              {Object.entries(chapterSummary?.EX?.[chapter]?.yearlyBreakdown || {})
                                .sort(([yearA], [yearB]) => yearB.localeCompare(yearA))
                                .map(([year, yearStats]) => (
                                  <Grid item xs={12} sm={6} key={`ex-${year}`}>
                                    <Paper sx={{
                                      p: 1.5,
                                      bgcolor: 'background.default',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <Typography variant="body2" color="text.secondary">
                                        {year}:
                                      </Typography>
                                      <Typography variant="body2" color="primary" fontWeight={500}>
                                        {yearStats.count} files
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                ))}
                            </Grid>
                          </Grid>

                          {/* Import Yearly Breakdown */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Import
                            </Typography>
                            <Grid container spacing={1}>
                              {Object.entries(chapterSummary?.IM?.[chapter]?.yearlyBreakdown || {})
                                .sort(([yearA], [yearB]) => yearB.localeCompare(yearA))
                                .map(([year, yearStats]) => (
                                  <Grid item xs={12} sm={6} key={`im-${year}`}>
                                    <Paper sx={{
                                      p: 1.5,
                                      bgcolor: 'background.default',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <Typography variant="body2" color="text.secondary">
                                        {year}:
                                      </Typography>
                                      <Typography variant="body2" color="primary" fontWeight={500}>
                                        {yearStats.count} files
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                ))}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ));
            })()}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const DetailedReport = () => {
    const [statusFilter, setStatusFilter] = useState('all');
    
    if (!reportData || !summaryStats || !imExSummary || !chapterSummary || !yearSummary) return null;

    const filteredData = reportData.filter(report => {
      if (statusFilter === 'all') return true;
      return (statusFilter === 'success' && report.successs) || 
             (statusFilter === 'failed' && !report.successs);
    });

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            Report: {selectedFile}
          </Typography>
        </Box>

        <SummarySection 
          summaryStats={summaryStats}
          imExSummary={imExSummary}
          chapterSummary={chapterSummary}
          yearSummary={yearSummary}
        />

        {/* Status Filter */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Detailed Records</Typography>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter by Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status ({reportData.length})</MenuItem>
              <MenuItem value="success">Success ({reportData.filter(r => r.successs).length})</MenuItem>
              <MenuItem value="failed">Failed ({reportData.filter(r => !r.successs).length})</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
            Showing {filteredData.length} records
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>File Name</TableCell>
                <TableCell>Chapter</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Excel Records</TableCell>
                <TableCell>JSON Records</TableCell>
                <TableCell>Success Count</TableCell>
                <TableCell>Failure Count</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((report, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Chip 
                      label={report.successs ? "Success" : "Failed"}
                      color={report.successs ? "success" : "error"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{report?.filePath?.split("/MASTERDATA/").pop()}</TableCell>
                  <TableCell>{report.chapter}</TableCell>
                  <TableCell>{report.chapType}</TableCell>
                  <TableCell>{report.totalExcelRecords}</TableCell>
                  <TableCell>{report.totalJSONRecords}</TableCell>
                  <TableCell>{report.elasticSuccessCount}</TableCell>
                  <TableCell>{report.elasticFailureCount}</TableCell>
                  <TableCell>
                    {!report.successs && (
                      Array.isArray(report.error) && report.error.length > 0
                        ? (report.error.join(', ').length > 200 
                            ? `${report.error.join(', ').substring(0, 200)}...`
                            : report.error.join(', '))
                        : (report.msg || 'No error message available')
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={() => {
                        const jsonString = JSON.stringify(report, null, 2);
                        const blob = new Blob([jsonString], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `report_${report.country}_${report.chapter}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                      }}
                      size="small"
                    >
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400
      }}>
        <CircularProgress 
          size={60}
          sx={{ mb: 2 }}
        />
        <Typography variant="h6" color="primary" gutterBottom>
          Loading Report
        </Typography>
        <Typography color="text.secondary">
          {loadingProgress.status}
        </Typography>
      </Box>
    );
  }

  return selectedFile ? <DetailedReport /> : <FileList />;
};

export default Reports; 